// apps/admin/src/stores/useAuthStore.ts
//
// Phase 8.1 Wave 2 Task 08 — admin portal canonical auth store.
//
// Why a Phase 8.1-local store on top of `@printway/ui`'s shared
// `useAuth()`:
//   - The shared store is wired to openapi-fetch and a Phase-1 mental
//     model (`requires2FA` login flow, `/auth/me` + `/auth/me/permissions`).
//     Phase 8.1 introduces the admin-portal-specific shape required by
//     wave 3+ pages: explicit `roles` list, `hasPermission(p)` selector,
//     and an `actor_role_hint='admin'`-flavoured bootstrap that calls
//     `/api/v1/me` (admin envelope) as per the task capsule.
//   - Keeping it admin-local avoids leaking admin-portal scope into the
//     shared package; merchant + vendor portals continue to consume
//     `@printway/ui`'s store unchanged.
//
// The store deliberately mirrors the shared one's surface as closely as
// possible so a future consolidation can collapse them with minimal
// caller churn.
//
// vg-binding: PLAN:task-08
// vg-binding: P8.D-160
// vg-binding: P8.D-166
// vg-binding: P8.D-260
// vg-binding: P8.D-267

import { useMemo } from 'react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { useAuth as useUiAuth } from '@printway/ui';

import { apiClient, ApiError, setUnauthorizedHandler } from '../lib/apiClient.js';
import { hasGrant } from '../lib/permission-match.js';
import { close as wsClose } from '../lib/wsClient.js';
import { queryClient } from '../lib/queryClient.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Wire status returned by `/me` (translation done server-side). */
export type AuthUserStatus =
  | 'Active'
  | 'Unverified'
  | 'Suspended'
  | 'Locked'
  | 'PendingApproval'
  | 'Inactive';

/**
 * Slim admin-portal mirror of the `/api/v1/me` response. Extend
 * cautiously — large PII surfaces belong behind dedicated profile
 * endpoints, not the home bootstrap.
 */
export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  primaryRole: string;
  activeRoleSlot: string;
  status: AuthUserStatus;
  language: string | null;
  themePreference: 'light' | 'dark' | 'system' | null;
  /** Additive role grants (e.g., admin user granted 'support_admin'). */
  roles: string[];
  /** Optional — only present on impersonation sessions. */
  actualUserId?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResult {
  requires2FA?: boolean;
  method?: string;
}

export interface AuthState {
  user: AuthUser | null;
  permissions: string[];
  permVersion: number;
  /**
   * True while a hydrate / login / refresh round-trip is in flight.
   * UI consumers gate their bootstrap spinner on `(isLoading && !user)`
   * so background refreshes don't unmount mid-action.
   */
  isLoading: boolean;
  /** Has the bootstrap `hydrate()` been attempted at least once? */
  hydrated: boolean;
  /** Last error code surfaced from a login / hydrate / logout call. */
  lastError: string | null;

  /** Bootstrap from `/api/v1/me` — called once on app boot. */
  hydrate: () => Promise<void>;
  /** Alias for `hydrate()` — matches the shared `useAuth` surface. */
  fetchMe: () => Promise<void>;
  login: (creds: LoginCredentials) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** True when the current user holds the named permission. */
  hasPermission: (permission: string) => boolean;
  /** True when the current user holds the named role (case-sensitive). */
  hasRole: (role: string) => boolean;
  /** Imperative reset — used by BroadcastChannel logout listener + 401 flow. */
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Internal envelope helpers — tolerate both modern + legacy shapes.
// ---------------------------------------------------------------------------

interface MePayload {
  user?: AuthUser;
  permissions?: string[];
  permVersion?: number;
  permsVersion?: number;
  // Sometimes the server returns the user fields at the top level.
  id?: string;
  email?: string;
}

function isAuthUser(value: unknown): value is AuthUser {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === 'string' && typeof v.email === 'string';
}

/**
 * Extract the canonical `(user, permissions, permVersion)` triple from
 * whatever `/me` returned. Tolerates:
 *   - `{ user, permissions, permVersion }`
 *   - `{ user, permissions, permsVersion }` (legacy snake)
 *   - top-level user payload `{ id, email, ... }` (older monolith)
 */
function extractMe(payload: unknown): {
  user: AuthUser | null;
  permissions: string[];
  permVersion: number;
} {
  if (!payload || typeof payload !== 'object') {
    return { user: null, permissions: [], permVersion: 0 };
  }
  // Read the canonical fields through `as Record<string, unknown>` so
  // type narrowing inside `if (isAuthUser(...))` branches doesn't
  // poison the union when we later look up MePayload-only keys.
  const env = payload as Record<string, unknown>;
  const nestedUser = env.user;
  let user: AuthUser | null = null;
  if (isAuthUser(nestedUser)) {
    user = nestedUser;
  } else if (isAuthUser(env)) {
    // top-level user (older /me shape)
    user = env as unknown as AuthUser;
  }
  const rawPermissions = env.permissions;
  const permissions = Array.isArray(rawPermissions)
    ? (rawPermissions as string[])
    : [];
  const rawPermVersion = env.permVersion;
  const rawPermsVersion = env.permsVersion;
  const permVersion =
    typeof rawPermVersion === 'number'
      ? rawPermVersion
      : typeof rawPermsVersion === 'number'
        ? rawPermsVersion
        : 0;
  return { user, permissions, permVersion };
}

// ---------------------------------------------------------------------------
// Permission matching (pure) — shared by the store method + the reactive hook
//
// dbg-20260725-uat-fin-2629-2632 / UAT-FIN-015 (#2632): the store method and
// `useHasPermission()` MUST apply identical semantics, otherwise a page that
// switches to the reactive hook silently changes its gate. Both call
// `hasGrant` from lib/permission-match.ts — the one matcher of the admin SPA,
// shared with the sidebar, Cmd+K and support pages (SEC-5).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/**
 * Canonical admin-portal auth store. Boot order:
 *   1. `useEffect(() => { useAuthStore.getState().hydrate(); }, [])`
 *      in App.tsx (or AdminAppShellLayout) on first render.
 *   2. On 401 the apiClient triggers `setUnauthorizedHandler` (wired
 *      below) which resets the store + leaves redirect to the consumer.
 */
// Dedup concurrent bootstraps. App.tsx AND AuthGuard both call fetchMe() on
// mount, and React StrictMode double-invokes effects in dev, so /auth/me +
// /auth/me/permissions fired 3-4× on every F5. Share one in-flight round-trip;
// cleared on settle so a later (sequential) call still refetches.
let inFlightHydrate: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  subscribeWithSelector((set, get) => ({
    user: null,
    permissions: [],
    permVersion: 0,
    isLoading: false,
    hydrated: false,
    lastError: null,

    async hydrate() {
      // Coalesce concurrent callers onto one round-trip (see inFlightHydrate).
      if (inFlightHydrate) return inFlightHydrate;
      inFlightHydrate = (async () => {
      set({ isLoading: true, lastError: null });
      try {
        // Delegate to the shared @printway/ui useAuth store so the two auth
        // stores share ONE /auth/me + /auth/me/permissions round-trip (both
        // fetchMe paths are in-flight-deduped). Then mirror its result into
        // this admin-local store so useAuthStore consumers (hasPermission,
        // Support pages) stay populated. Previously each store did its own HTTP
        // → the pair fired 4× per F5 (2 stores × StrictMode). debug 2026-07-14.
        await useUiAuth.getState().fetchMe();
        const ui = useUiAuth.getState();
        set({
          user: (ui.user as unknown as AuthUser | null) ?? null,
          permissions: Array.isArray(ui.permissions) ? ui.permissions : [],
          permVersion: ui.permVersion ?? 0,
          isLoading: false,
          hydrated: true,
          lastError: ui.lastError ?? null,
        });
      } catch (err) {
        // 401 → user stays null (consumer redirects to /login).
        // Any other failure → record the code so the UI can surface it.
        const code =
          err instanceof ApiError
            ? err.code
            : err instanceof Error
              ? err.message
              : 'HYDRATE_FAILED';
        set({
          user: null,
          permissions: [],
          permVersion: 0,
          isLoading: false,
          hydrated: true,
          lastError: code,
        });
      }
      })();
      try {
        return await inFlightHydrate;
      } finally {
        inFlightHydrate = null;
      }
    },

    async fetchMe() {
      return get().hydrate();
    },

    async login(creds) {
      set({ isLoading: true, lastError: null });
      try {
        const payload = await apiClient.post<unknown>('/auth/login', creds);
        const p = (payload ?? {}) as MePayload & {
          requires2FA?: boolean;
          method?: string;
        };

        // Two-step path — 2FA required.
        if (p.requires2FA) {
          set({ isLoading: false });
          const result: LoginResult = { requires2FA: true };
          if (p.method !== undefined) result.method = p.method;
          return result;
        }

        // Direct success — cookies are already set by the server.
        // If the login response inlines the user, hydrate immediately;
        // otherwise fall back to /me.
        if (p.user || isAuthUser(p)) {
          const { user, permissions, permVersion } = extractMe(p);
          set({
            user,
            permissions,
            permVersion,
            isLoading: false,
            hydrated: true,
          });
        } else {
          set({ isLoading: false });
          await get().hydrate();
        }
        return {};
      } catch (err) {
        const code =
          err instanceof ApiError
            ? err.code
            : err instanceof Error
              ? err.message
              : 'LOGIN_FAILED';
        set({ isLoading: false, lastError: code });
        throw err;
      }
    },

    async logout() {
      set({ isLoading: true });
      try {
        await apiClient.post('/auth/logout');
      } catch {
        // Even if the network call fails we drop local state — the
        // cookie will expire server-side regardless.
      } finally {
        // Tear down WS first so the next session starts clean.
        try {
          wsClose();
        } catch {
          /* ignore */
        }
        get().reset();
      }
    },

    async refresh() {
      try {
        await apiClient.post('/auth/refresh', undefined, {
          skipAuthRefresh: true,
        });
        await get().hydrate();
      } catch (err) {
        const code =
          err instanceof ApiError
            ? err.code
            : err instanceof Error
              ? err.message
              : 'REFRESH_FAILED';
        set({ lastError: code });
      }
    },

    hasPermission(permission: string): boolean {
      // Imperative read — safe from event handlers / effects. React render
      // paths MUST use `useHasPermission()` instead so the component
      // re-renders once hydrate() lands (see that hook's docblock).
      return hasGrant(get().permissions, permission);
    },

    hasRole(role: string): boolean {
      const user = get().user;
      if (!user) return false;
      if (user.primaryRole === role) return true;
      if (user.activeRoleSlot === role) return true;
      return user.roles?.includes(role) ?? false;
    },

    reset() {
      set({
        user: null,
        permissions: [],
        permVersion: 0,
        isLoading: false,
        // Must drop with the user: App.tsx re-hydrates only `!hydrated`, so a
        // reset that kept `true` left the NEXT account on the previous one's
        // permissions until F5 (vendor products modal, 2026-10-12: viewer →
        // logout → superadmin in one tab = every switch disabled, no pencil).
        hydrated: false,
        lastError: null,
      });
      // Wipe the TanStack Query cache so the next account never sees the
      // previous user's data (profile, lists, /me) still cached on screen
      // after a re-login (dbg-20260720). reset() is the single teardown path
      // shared by logout(), the 401 handler, and the BroadcastChannel logout
      // listener, so clearing here covers every session-end.
      try {
        queryClient.clear();
      } catch {
        /* cache clear is best-effort — never block logout on it */
      }
    },
  })),
);

// ---------------------------------------------------------------------------
// Wire the 401 handler so a failed silent-refresh resets the store.
// AuthGuard / AdminAppShellLayout pick up `user === null` and redirect.
// ---------------------------------------------------------------------------

setUnauthorizedHandler(() => {
  useAuthStore.getState().reset();
});

// ---------------------------------------------------------------------------
// Follow the shared @printway/ui session.
//
// Login, the topbar logout and the BroadcastChannel logout all go through the
// SHARED store; this admin-local one only learned about a session change if a
// layout effect happened to re-hydrate it. Logout never reset it, so the next
// account inherited the previous account's permissions (2026-10-12). Track the
// shared user id instead: gone → reset, different → mirror the shared state.
// ---------------------------------------------------------------------------

function uiUserId(u: unknown): string | null {
  const id = (u as { id?: unknown } | null | undefined)?.id;
  return typeof id === 'string' && id !== '' ? id : null;
}

useUiAuth.subscribe((state, prev) => {
  const next = uiUserId(state.user);
  const idChanged = next !== uiUserId(prev.user);
  if (next === null) {
    if (!idChanged) return;
    const local = useAuthStore.getState();
    if (local.user !== null || local.permissions.length > 0) local.reset();
    return;
  }
  // CR2-02: the SAME user can get new permissions — a Retry after a failed
  // permissions call, `refresh()` after a permVersion bump, fetchMe() from
  // Profile/2FA. Mirroring only on an id change left this store on the old
  // (often empty) list until F5. Skip half-finished loads: the `set` that
  // ends the load fires this listener again with the real answer.
  if (state.isLoading) return;
  if (
    !idChanged &&
    state.permVersion === prev.permVersion &&
    state.permissions === prev.permissions &&
    state.lastError === prev.lastError
  ) {
    return;
  }
  // Mirror, don't hydrate (CR1-01): the shared store just finished its own
  // /auth/me + /auth/me/permissions round-trip — that `set` is what fired this
  // listener — so its state IS the answer. hydrate() here opened a SECOND
  // round-trip, and on a direct A→B switch left A's permissions in this store
  // until it landed.
  useAuthStore.setState({
    user: (state.user as unknown as AuthUser | null) ?? null,
    permissions: Array.isArray(state.permissions) ? state.permissions : [],
    permVersion: state.permVersion ?? 0,
    isLoading: false,
    hydrated: true,
    lastError: state.lastError ?? null,
  });
});

// ---------------------------------------------------------------------------
// Reactive permission hook
//
// dbg-20260725-uat-fin-2629-2632 — UAT-FIN-015 (#2632) bug:
//   `const hasPermission = useAuthStore((s) => s.hasPermission)` selects the
//   METHOD, whose identity never changes, so the component does NOT re-subscribe
//   to `permissions`. On a hard page load (direct URL / F5) the first render
//   happens while `permissions === []` (hydrate() is still in flight) and the
//   component never re-renders when it resolves → permission-gated UI stays
//   hidden forever. Observed: /admin/billing/payment-requests rendered
//   "Bạn không có quyền tạo PR." on hard-load (create button count 0) while an
//   in-app SPA navigation to the same route rendered it fine (count 1), even
//   though /auth/me/permissions returned `finance:create_payment_request` both
//   times.
//
// This hook subscribes to `permissions` + `permVersion`, so the returned
// predicate identity changes when either does and every consumer re-renders.
// Use it in ANY render path that gates UI on a permission.
// ---------------------------------------------------------------------------

/**
 * Reactive permission predicate. Prefer this over
 * `useAuthStore((s) => s.hasPermission)` inside components — see module note.
 *
 * @example
 *   const hasPermission = useHasPermission();
 *   const canCreate = hasPermission(PERMISSIONS.CREATE_PAYMENT_REQUEST);
 */
export function useHasPermission(): (permission: string) => boolean {
  const permissions = useAuthStore((s) => s.permissions);
  const permVersion = useAuthStore((s) => s.permVersion);
  return useMemo(
    () => (permission: string) => hasGrant(permissions, permission),
    // `permVersion` is a deliberate cache-buster, not a value the callback
    // reads: bumping it must mint a NEW predicate identity so every consumer
    // re-renders after a permission refresh (see the module note above —
    // dropping it reintroduces the hard-load "Bạn không có quyền tạo PR." bug).
    // ESLint can only see that the body never dereferences it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions, permVersion],
  );
}

export default useAuthStore;
